/**
 * "Case studies page" content — a singleton (like homePage/aboutPage), covering
 * only the listing page's own chrome (hero, "proof" section intro, bottom CTA
 * banner). Deliberately does NOT duplicate the cards/categories/stats/detail
 * pages — those are already fully CRUD-editable via the existing `caseStudies`
 * CMS section (src/admin/editors/CaseStudiesEditor.jsx), which the admin editor
 * for this page embeds directly rather than re-implementing.
 * DEFAULT_PAGE_CONTENT below IS the page's original hardcoded copy
 * (src/components/case-studies/caseStudies.jsx), so the CMS-driven page
 * renders byte-identical to what shipped before.
 */
export const DEFAULT_PAGE_CONTENT = {
  hiddenSections: [],
  hero: {
    headlinePrefix: 'Real results for',
    headlineAccent: 'real businesses',
    description:
      'From predictive operations to customer-facing AI, we ship systems that hold up in production - measurable latency, clear ROI, and teams that actually adopt them.',
  },
  proof: {
    headlinePrefix: 'Proof in the',
    headlineAccent: 'numbers',
    description:
      'Explore how teams across industries use Databiqs to turn complex data and workflows into reliable, human-centered AI outcomes.',
  },
  ctaBanner: {
    title: 'See how this works for your business',
    subtitle: 'Schedule a free 30-minute strategy call with one of our senior architects.',
    ctaLabel: 'Book a Consultation',
    ctaTo: '/book-consultation',
  },
};

function deepClone(value) {
  return JSON.parse(JSON.stringify(value));
}

export function createCaseStudiesPageTemplate() {
  return deepClone(DEFAULT_PAGE_CONTENT);
}

export function normalizeCaseStudiesPageData(stored) {
  const tpl = createCaseStudiesPageTemplate();
  if (!stored || typeof stored !== 'object') return tpl;

  return {
    hiddenSections: Array.isArray(stored.hiddenSections) ? stored.hiddenSections : tpl.hiddenSections,
    hero: { ...tpl.hero, ...(stored.hero || {}) },
    proof: { ...tpl.proof, ...(stored.proof || {}) },
    ctaBanner: { ...tpl.ctaBanner, ...(stored.ctaBanner || {}) },
  };
}

export function normalizeCaseStudiesPageStyles(stored) {
  if (!stored || typeof stored !== 'object') return {};
  const out = {};
  for (const [blockId, val] of Object.entries(stored)) {
    if (!val || typeof val !== 'object') continue;
    out[blockId] = {
      desktop: val.desktop && typeof val.desktop === 'object' ? { ...val.desktop } : {},
      mobile: val.mobile && typeof val.mobile === 'object' ? { ...val.mobile } : {},
    };
  }
  return out;
}

/** Full normalized { data, styles } shape, the way it's stored under content.caseStudiesPage. */
export function normalizeCaseStudiesPage(stored) {
  return {
    data: normalizeCaseStudiesPageData(stored?.data),
    styles: normalizeCaseStudiesPageStyles(stored?.styles),
    updatedAt: stored?.updatedAt || new Date().toISOString(),
  };
}
